import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import queryString from 'query-string';

import BaseFilter, { FilterTypeEnum } from 'components/base/BaseFilter';
import { DISPLAY_SIGN_UP_CHANNEL, MonthOptions, SEGMENT_TYPE, SIGN_UP_CHANNEL } from 'config/CustomEnums';
import {
  CustomerStatus,
} from 'models/CustomerListModel';

import { ArrayStringData, createAction, getHashKeyString } from 'utils';

import './Filter.scss';
import { tierTypeConfig } from 'config/TierTypeConfig';

function Filter({ backAction = () => { } }) {
  const dispatch = useDispatch();
  const location = useLocation();

  const genderSource = [
    { name: 'Male', pk: 'MALE' },
    { name: 'Female', pk: 'FEMALE' },
    { name: 'Not disclosed', pk: 'NOT_DISCLOSED' },
  ];

  const { segmentList, levelSource } = useSelector((state) => ({
    segmentList: state.segments.notPagedAllList,
    levelSource: state.levels.levelList,
    filters: state.customerList.filters,
  }));

  const [staticSegmentList, dynamicSegmentList] = useMemo(() => {
    if (!segmentList?.length > 0) {
      return [[], []]
    }
    const staticSegment = segmentList?.filter(item => item?.segmentType === SEGMENT_TYPE.STATIC) || []
    const dynamicSegment = segmentList?.filter(item => item?.segmentType === SEGMENT_TYPE.DYNAMIC) || []
    return [staticSegment, dynamicSegment]
  }, [segmentList])

  const activeTab = getHashKeyString(location.hash);

  const parsedSearch = queryString.parse(location.search);
  const searchLevels = parsedSearch['levels'] || '';
  const searchSegments = parsedSearch['segments'] || '';
  const searchAdditionalData = parsedSearch['additional_data'] || '';
  const searchGenders = parsedSearch['gender'] || '';
  const searchAge = parsedSearch['age'] || '';
  const searchBirthMonth = parsedSearch['birth_month'] || '';
  const searchCreationPeriod = parsedSearch['create_date'] || '';
  const searchTimeRange = parsedSearch['time_range'] || '';
  const searchReportType = parsedSearch['report_type'] || '';
  const searchSignUpChannel = parsedSearch['sign_up_channel'] || '';
  const searchStatus = parsedSearch['status'] || '';
  const searchStaticSegments = parsedSearch['staticSegmentsIn'] || '';
  const searchDynamicSegments = parsedSearch['dynamicSegmentsIn'] || '';

  const [age, setAge] = useState(() => {
    const ages = ArrayStringData(searchAge);
    if (ages.length > 0) return ages;
    return [0, 0];
  });
  const [genders, setGenders] = useState(ArrayStringData(searchGenders));

  const [segmentType, setSegmentType] = useState(ArrayStringData(searchSegments));
  const [additionalData, setAdditionalData] = useState(searchAdditionalData);
  const [levels, setLevels] = useState(ArrayStringData(searchLevels));

  const [birthMonth, setBirthMonth] = useState(() => {
    return searchBirthMonth
      .split(',')
      .filter(
        (ele, index, array) => ele !== '' && array.indexOf(ele) === index,
      );
  });
  const [creationPeriod, setCreationPeriod] = useState(searchCreationPeriod);
  const [reportsTimeRange, setReportsTimeRange] = useState(searchTimeRange);
  const [reportType, setReportType] = useState(searchReportType);
  const [signUpChannel, setSignUpChannel] = useState(
    searchSignUpChannel,
  );
  const [status, setStatus] = useState(ArrayStringData(searchStatus));
  const [dynamicSegments, setDynamicSegments] = useState(ArrayStringData(searchDynamicSegments));
  const [staticSegments, setStaticSegments] = useState(ArrayStringData(searchStaticSegments));

  useEffect(() => {
    if (activeTab !== 'Reports') {
      dispatch(createAction('levels/getLevelList')({}));
      dispatch(
        createAction('segments/getAllList')({
          type: 'all',
          isSelectorLoad: true,
        }),
      );
    }
  }, [activeTab, dispatch]);

  let content;
  if (activeTab === 'Reports') {
    content = [
      {
        title: 'Report type',
        data: [
          { pk: 'WEEKLY', name: 'Weekly report' },
          { pk: 'MONTHLY', name: 'Monthly report' },
        ],
        value: reportType,
        setValue: setReportType,
        component: FilterTypeEnum.singleChoice,
        className: 'fit-content-height-area',
      },
      {
        title: 'Time range',
        value: reportsTimeRange,
        setValue: setReportsTimeRange,
        component: FilterTypeEnum.dateRange,
      },
    ];
  } else {
    content = [
      {
        hide: !tierTypeConfig.segments,
        title: 'Segments',
        data: [
          {
            name: 'Dynamic segment',
            pk: 'dynamicSegmentsIn',
            subComponment: {
              data: dynamicSegmentList,
              value: dynamicSegments,
              setValue: setDynamicSegments,
              component: FilterTypeEnum.choice,
              className: 'filter-list-items-sub-area',
            },
          },
          {
            name: 'Static segment',
            pk: 'staticSegmentsIn',
            subComponment: {
              data: staticSegmentList,
              value: staticSegments,
              setValue: setStaticSegments,
              component: FilterTypeEnum.choice,
              className: 'filter-list-items-sub-area',
            },
          },
        ],
        value: segmentType,
        setValue: setSegmentType,
        component: FilterTypeEnum.singleChoiceTargetCustomer,
      },
      {
        title: 'Additional data',
        placeholder: 'Search by Title and Value',
        value: additionalData,
        setValue: setAdditionalData,
        component: FilterTypeEnum.inputWithTitle,
      },
      {
        hide: !tierTypeConfig.levels,
        title: 'Levels',
        data: levelSource,
        value: levels,
        setValue: setLevels,
        component: FilterTypeEnum.choice,
      },
      {
        title: 'Status',
        data: [
          { name: 'Active', pk: CustomerStatus.active },
          // { name: 'Pre-Active', pk: CustomerStatus.preActive },
          { name: 'Temporary', pk: CustomerStatus.temporary },
          { name: 'Temporary Earning', pk: CustomerStatus.temporaryEarning },
          { name: 'Deactive', pk: CustomerStatus.deactive },
          { name: 'Requested to Del', pk: CustomerStatus.deleteRequested },
        ],
        value: status,
        setValue: setStatus,
        component: FilterTypeEnum.choice,
      },
      {
        title: 'Sign up channel',
        data: [
          {
            name: DISPLAY_SIGN_UP_CHANNEL.APP,
            pk: SIGN_UP_CHANNEL.APP,
          },
          {
            name: DISPLAY_SIGN_UP_CHANNEL.WEBSITE,
            pk: SIGN_UP_CHANNEL.WEBSITE,
          },
          {
            name: DISPLAY_SIGN_UP_CHANNEL.IMPORTED_BY_CSV,
            pk: SIGN_UP_CHANNEL.IMPORTED_BY_CSV,
          },
          {
            name: DISPLAY_SIGN_UP_CHANNEL.ONLINE_STORE,
            pk: SIGN_UP_CHANNEL.ONLINE_STORE,
          },
          {
            name: DISPLAY_SIGN_UP_CHANNEL.OFFLINE_STORE,
            pk: SIGN_UP_CHANNEL.OFFLINE_STORE,
          },
          {
            name: DISPLAY_SIGN_UP_CHANNEL.WECHAT,
            pk: SIGN_UP_CHANNEL.WECHAT,
          },
        ].filter(option => {
          if (!tierTypeConfig.onlineStore && option.pk === SIGN_UP_CHANNEL.ONLINE_STORE) {
            return false;
          };
          return true;
        }),
        value: signUpChannel,
        setValue: setSignUpChannel,
        component: FilterTypeEnum.singleChoice,
      },
      {
        title: 'Gender',
        data: genderSource,
        value: genders,
        setValue: setGenders,
        component: FilterTypeEnum.choice,
      },
      {
        title: 'Age',
        data: [0, 80],
        value: age,
        setValue: setAge,
        component: FilterTypeEnum.slider,
      },
      // {
      //   title: 'Birthday start date',
      //   value: startDate,
      //   setValue: setStartDate,
      //   component: FilterTypeEnum.date,
      // },
      // {
      //   title: 'Birthday end date',
      //   value: endDate,
      //   setValue: setEndDate,
      //   component: FilterTypeEnum.date,
      // },
      {
        title: 'Birth month',
        data: MonthOptions,
        value: birthMonth,
        setValue: setBirthMonth,
        component: FilterTypeEnum.choice,
      },
      {
        title: 'Create date',
        value: creationPeriod,
        setValue: setCreationPeriod,
        component: FilterTypeEnum.dateRange,
      },
    ];
  }

  return <BaseFilter content={content} backAction={backAction} />;
}

export default Filter;
